export interface StatusMatchOptions {
  mspType?: string;
  strictMatch?: boolean;
}

export function normalizeStatusValue(value: string | number | null | undefined): string {
  return String(value || "").toLowerCase().trim();
}

function getAutotaskStatusCategories(normalizedStatus: string): Set<string> {
  const categories = new Set<string>();

  if (normalizedStatus.includes("new")) {
    categories.add("open");
  }

  if (normalizedStatus.includes("in progress") || normalizedStatus.includes("scheduled")) {
    categories.add("in progress");
  }

  if (
    normalizedStatus.includes("complete") ||
    normalizedStatus.includes("closed") ||
    normalizedStatus.includes("approved") ||
    normalizedStatus.includes("denied")
  ) {
    categories.add("closed");
  }

  if (normalizedStatus.includes("resolved") || normalizedStatus.includes("complete")) {
    categories.add("resolved");
  }

  if (normalizedStatus.includes("escalat")) {
    categories.add("escalated");
  }

  if (normalizedStatus.includes("waiting")) {
    categories.add("on hold");
  }

  if (categories.size === 0) {
    categories.add("unknown");
  }

  return categories;
}

function getDefaultStatusCategories(normalizedStatus: string): Set<string> {
  const categories = new Set<string>();

  if (normalizedStatus.includes("new") || normalizedStatus.includes("open")) {
    categories.add("open");
  }

  if (
    normalizedStatus.includes("in progress") ||
    normalizedStatus.includes("progress") ||
    normalizedStatus.includes("assigned") ||
    normalizedStatus.includes("responded")
  ) {
    categories.add("in progress");
  }

  if (
    normalizedStatus.includes("closed") ||
    normalizedStatus.includes("approved") ||
    normalizedStatus.includes("denied") ||
    normalizedStatus.includes("complete")
  ) {
    categories.add("closed");
  }

  if (normalizedStatus.includes("resolved") || normalizedStatus.includes("complete")) {
    categories.add("resolved");
  }

  if (normalizedStatus.includes("escalat") || normalizedStatus.includes("immediate") || normalizedStatus.includes("urgent")) {
    categories.add("escalated");
  }

  if (normalizedStatus.includes("hold") || normalizedStatus.includes("waiting")) {
    categories.add("on hold");
  }

  if (categories.size === 0) {
    categories.add("unknown");
  }

  return categories;
}

export function getTicketStatusCategories(
  status: string | null | undefined,
  mspType?: string
): Set<string> {
  const normalizedStatus = normalizeStatusValue(status);
  if (!normalizedStatus) {
    return new Set(["unknown"]);
  }

  if (normalizeStatusValue(mspType) === "autotask") {
    return getAutotaskStatusCategories(normalizedStatus);
  }

  return getDefaultStatusCategories(normalizedStatus);
}

const CLOSED_STATUS_CATEGORIES = new Set(["closed", "resolved"]);

/** Statuses checked on the Service Desk filter before the user changes it. */
export function defaultOpenStatusSelection(statuses: string[], mspType?: string): string[] {
  const open = statuses
    .map((status) => String(status || "").trim())
    .filter((status) => Boolean(status) && isOpenTicketStatus(status, mspType));
  return open.length > 0 ? Array.from(new Set(open)) : statuses.filter(Boolean);
}

/** Open work stays visible. Closed, completed, and resolved statuses do not. */
export function isOpenTicketStatus(
  status: string | null | undefined,
  mspType?: string
): boolean {
  const categories = getTicketStatusCategories(status, mspType);
  let open = true;
  categories.forEach((category) => {
    if (CLOSED_STATUS_CATEGORIES.has(category)) {
      open = false;
    }
  });
  return open;
}

export function matchesSelectedStatus(
  status: string | null | undefined,
  selectedStatuses: string[] | undefined,
  options: StatusMatchOptions = {}
): boolean {
  if (selectedStatuses === undefined) {
    return true;
  }

  if (!Array.isArray(selectedStatuses) || selectedStatuses.length === 0) {
    return false;
  }

  const normalizedSelectedStatuses = new Set(
    selectedStatuses.map((value) => normalizeStatusValue(value)).filter(Boolean)
  );
  if (normalizedSelectedStatuses.size === 0) {
    return false;
  }

  const normalizedStatus = normalizeStatusValue(status);
  if (normalizedStatus && normalizedSelectedStatuses.has(normalizedStatus)) {
    return true;
  }

  if (options.strictMatch) {
    return false;
  }

  const categories = getTicketStatusCategories(status, options.mspType);
  let hasCategoryMatch = false;
  categories.forEach((category) => {
    if (normalizedSelectedStatuses.has(category)) {
      hasCategoryMatch = true;
    }
  });
  return hasCategoryMatch;
}
